import React, { useState, useEffect, useRef } from 'react';
import { Battery, Signal, Thermometer, WifiOff, Droplets, GripHorizontal, Clock, Bell, AlarmClock } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Sensor } from '../../types';
import { useGetLiveDataQuery } from '../../services/sensorDataApi';
import { getBatteryStatus } from '../../utils/batteryUtils';
import { getSignalStatus } from '../../utils/signalUtils';
import { formatSensorSerial, isNewerVersion } from '../../utils/sensorUtils';
import { formatDateTime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { LiveDataResponse } from '../../types/sensorData';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { useAcknowledgeAlarmLogMutation } from '../../services/alarmApi';
import ReactDOM from 'react-dom';
import { alarmApi } from '../../services/alarmApi';
import { useGetAlarmsQuery } from '../../services/alarmApi';

interface SensorTableProps {
  sensors: Sensor[];
  alarmLog: any[];
  alarmSettingsMap?: { [sensorId: number]: any };
  onUpdateFirmware?: (sensorId: number) => void;
  showFirmwareUpdate?: boolean;
  latestFirmwareVersion?: string | null;
  onEdit?: (sensor: Sensor) => void;
  onDelete?: (sensor: Sensor) => void;
  enableDragAndDrop?: boolean;
}

interface SortableSensorRowProps {
  sensor: Sensor;
  liveData?: LiveDataResponse;
  onRowClick: (sensorId: number) => void;
  provided: any;
  isDragging: boolean;
  latestAlarm?: any;
  preAlarm?: any;
  preAlarmTimeLeft?: number;
  onMouseEnter?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  onMouseLeave?: () => void;
  onUpdateFirmware?: (sensorId: number) => void;
  showFirmwareUpdate?: boolean;
  latestFirmwareVersion: string | null;
  onEdit?: (sensor: Sensor) => void;
  onDelete?: (sensor: Sensor) => void;
  enableDragAndDrop?: boolean;
}

function getSensorAlarmState(sensorId: number, alarmLog: any[]): 'alarm' | 'normal' {
  const logs = alarmLog.filter(log => log.sensor_id === sensorId);
  if (logs.some(log => log.active && !log.acknowledged && !log.alarm_reset)) {
    return 'alarm';
  }
  return 'normal';
}

// Add CSS for bell animation
const bellStyles = `
@keyframes bell-flash-red {
  0%, 100% { color: #ef4444; opacity: 1; }
  50% { color: #b91c1c; opacity: 0.3; }
}
@keyframes bell-flash-blue {
  0%, 100% { color: #3b82f6; opacity: 1; }
  50% { color: #1e40af; opacity: 0.3; }
}
@keyframes bell-flash-orange {
  0%, 100% { color: #f59e42; opacity: 1; }
  50% { color: #b45309; opacity: 0.3; }
}
.bell-flash-red {
  animation: bell-flash-red 1s infinite;
}
.bell-flash-blue {
  animation: bell-flash-blue 1s infinite;
}
.bell-flash-orange {
  animation: bell-flash-orange 1s infinite;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('bell-flash-styles')) {
  const style = document.createElement('style');
  style.id = 'bell-flash-styles';
  style.innerHTML = bellStyles;
  document.head.appendChild(style);
}

// Helper to format mm:ss
function formatCountdown(ms: number) {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

// Tooltip/modal for alarm details
function AlarmTooltip({ alarmDetails, position, onAcknowledge, isLoading, isSuccess, onMouseEnter, onMouseLeave, isPreAlarm, preAlarmTimeLeft }: any) {
  if (!alarmDetails) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1000,
        background: 'rgba(30,30,30,0.98)',
        color: 'white',
        borderRadius: 8,
        padding: '12px 18px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        minWidth: 220,
        pointerEvents: 'auto',
      }}
      className="text-xs"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="font-bold mb-1">{isPreAlarm ? 'Pre-Alarm Details' : 'Alarm Details'}</div>
      <div><span className="font-semibold">Activated:</span> {alarmDetails.activated ? new Date(alarmDetails.activated).toLocaleString() : '-'}</div>
      <div><span className="font-semibold">Value:</span> {alarmDetails.alarm_values}</div>
      <div><span className="font-semibold">Type:</span> {alarmDetails.alarm_type}</div>
      {isPreAlarm ? (
        <>
          <div><span className="font-semibold">Pre-Alarm:</span> Yes</div>
          <div><span className="font-semibold">Time until active:</span> {formatCountdown(preAlarmTimeLeft || 0)}</div>
        </>
      ) : (
        <>
          <div><span className="font-semibold">Active:</span> {alarmDetails.active ? 'Yes' : 'No'}</div>
          <div><span className="font-semibold">Acknowledged:</span> {alarmDetails.acknowledged ? 'Yes' : 'No'}</div>
          <div><span className="font-semibold">Reset:</span> {alarmDetails.alarm_reset ? new Date(alarmDetails.alarm_reset).toLocaleString() : 'No'}</div>
          <div className="mt-2">
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold disabled:opacity-60"
              onClick={onAcknowledge}
              disabled={isLoading || isSuccess || alarmDetails.acknowledged}
            >
              {isLoading ? 'Acknowledging...' : isSuccess ? 'Acknowledged!' : alarmDetails.acknowledged ? 'Already Acknowledged' : 'Acknowledge'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SortableSensorRow({ sensor, liveData, onRowClick, provided, isDragging, latestAlarm, preAlarm, preAlarmTimeLeft, onMouseEnter, onMouseLeave, onUpdateFirmware, showFirmwareUpdate, latestFirmwareVersion, onEdit, onDelete, enableDragAndDrop }: SortableSensorRowProps & { enableDragAndDrop?: boolean }) {
  const reading = liveData?.find(d => Number(d.sensor_id) === sensor.serialNo);

  let rowColor = '';
  let bell = null;
  if (preAlarm) {
    rowColor = 'bg-orange-100/30 dark:bg-orange-500/20';
    bell = <Bell className="h-4 w-4 bell-flash-orange text-orange-500 ml-2" />;
  } else if (latestAlarm) {
    const alarmType = String(latestAlarm.alarm_type);
    if (alarmType === 'low') {
      rowColor = 'bg-blue-200/20 dark:bg-blue-500/30';
      bell = <Bell className="h-4 w-4 bell-flash-blue text-blue-500 ml-2" />;
    } else if (alarmType === 'high') {
      rowColor = 'bg-red-200/20 dark:bg-red-500/30';
      bell = <Bell className="h-4 w-4 bell-flash-red text-red-500 ml-2" />;
    } else {
      // Any other alarm state: solid red bell
      rowColor = 'bg-red-200/20 dark:bg-red-500/30';
      bell = <Bell className="h-4 w-4 text-red-500 ml-2" />;
    }
  } else {
    bell = <Bell className="h-4 w-4 text-green-500 ml-2" />;
  }

  const isUpdateAvailable = () => {
    return isNewerVersion(latestFirmwareVersion || '', sensor.firmware_version || '');
  };

  // Track if the row is being dragged to prevent click navigation
  const dragRef = useRef(false);

  return (
    <>
      <tr
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={`group hover:bg-gray-50 dark:hover:bg-gray-800 ${isDragging ? 'shadow-lg bg-gray-50 dark:bg-gray-700' : ''} ${rowColor}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={enableDragAndDrop ? (e => {
          if (!dragRef.current) onRowClick(sensor.id);
        }) : undefined}
        style={enableDragAndDrop ? { cursor: 'pointer' } : {}}
        onMouseDown={enableDragAndDrop ? () => { dragRef.current = false; } : undefined}
        onDragStart={enableDragAndDrop ? () => { dragRef.current = true; } : undefined}
        onDragEnd={enableDragAndDrop ? () => { setTimeout(() => { dragRef.current = false; }, 0); } : undefined}
      >
        <td className="px-1 sm:px-4 py-2 sm:py-4 w-[35%] sm:w-auto">
          <div className="flex items-center min-w-0">
            {/* Drag handle: only this is draggable */}
            {enableDragAndDrop && (
              <div
                {...provided.dragHandleProps}
                className="p-1 sm:p-2 mr-1 sm:mr-2 cursor-grab sm:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity flex-shrink-0"
                title="Drag to reorder"
                aria-label="Drag to reorder"
              >
                {/* SVG for six dots */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-grip-vertical h-4 w-4 text-gray-400">
                  <circle cx="12" cy="9" r="1"></circle>
                  <circle cx="19" cy="9" r="1"></circle>
                  <circle cx="5" cy="9" r="1"></circle>
                  <circle cx="12" cy="15" r="1"></circle>
                  <circle cx="19" cy="15" r="1"></circle>
                  <circle cx="5" cy="15" r="1"></circle>
                </svg>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                {sensor.name}
              </div>
              <div className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                {formatSensorSerial(sensor.serialNo, sensor.type)}
              </div>
            </div>
          </div>
        </td>
        <td className="px-1 sm:px-4 py-2 sm:py-4 w-[30%] sm:w-auto">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center">
              <Thermometer className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-900 dark:text-white truncate">
                {sensor.type === '5'
                  ? `${
                      (typeof reading?.temperature === 'number' && !isNaN(reading.temperature))
                        ? reading.temperature.toFixed(1) + '°C'
                        : '----'
                    } / ${
                      (typeof reading?.temperature_2 === 'number' && !isNaN(reading.temperature_2))
                        ? reading.temperature_2.toFixed(1) + '°C'
                        : '----'
                    }`
                  : (
                    (typeof reading?.temperature === 'number' && !isNaN(reading.temperature))
                      ? `${reading.temperature.toFixed(1)}°C`
                      : '----'
                  )
                }
              </span>
              {bell}
              {preAlarm && (
                <span className="ml-2 text-xs text-orange-600 font-semibold flex items-center">
                  <AlarmClock className="h-4 w-4 mr-1 text-orange-500" />
                  {formatCountdown(preAlarmTimeLeft ?? 0)}
                </span>
              )}
            </div>
            {sensor.type === '3' && reading?.humidity !== undefined && (
              <div className="flex items-center mt-1">
                <Droplets className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-900 dark:text-white truncate">
                  {reading.humidity.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </td>
        <td className="px-1 sm:px-4 py-2 sm:py-4 w-[20%] sm:w-auto">
          {sensor.type === '4' || sensor.type === '5' ? (
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-semibold">Legacy</span>
          ) : (
            <div className="flex items-center space-x-1 sm:space-x-4">
              <div className="flex items-center flex-shrink-0">
                <Signal className={`h-3 w-3 sm:h-4 sm:w-4 ${getSignalStatus(sensor.SNR).iconColor}`} aria-label={getSignalStatus(sensor.SNR).text} />
              </div>
              <div className="flex items-center flex-shrink-0">
                <Battery className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                {!isNaN(parseFloat(sensor.Battery)) && (
                  <span className={`ml-0.5 text-[10px] sm:text-sm ${getBatteryStatus(sensor.type, parseFloat(sensor.Battery)).color}`}>
                    {getBatteryStatus(sensor.type, parseFloat(sensor.Battery)).text}
                  </span>
                )}
              </div>
            </div>
          )}
        </td>
        <td className="hidden md:table-cell px-1 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            {reading?.timestamp ? formatDateTime(reading.timestamp, true) : 'Never'}
          </div>
        </td>
        <td className="px-1 sm:px-4 py-2 sm:py-4 w-[15%] sm:w-auto text-right">
          <div className="flex flex-col items-end space-y-1">
            {sensor.firmware_version && (
              <div className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 truncate">
                FW: {sensor.firmware_version}
              </div>
            )}
            {showFirmwareUpdate && isUpdateAvailable() && onUpdateFirmware ? (
              <button
                className="mt-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                onClick={e => {
                  e.stopPropagation();
                  onUpdateFirmware(sensor.id);
                }}
              >
                Update Firmware
              </button>
            ) : null}
            {onEdit && (
              <button
                className="mt-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                onClick={e => {
                  e.stopPropagation();
                  onEdit(sensor);
                }}
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                className="mt-1 px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                onClick={e => {
                  e.stopPropagation();
                  onDelete(sensor);
                }}
              >
                Delete
              </button>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}

export default function SensorTable({ sensors, alarmLog, alarmSettingsMap = {}, onUpdateFirmware, showFirmwareUpdate = false, latestFirmwareVersion = null, onEdit, onDelete, enableDragAndDrop = false }: SensorTableProps) {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const [orderedSensors, setOrderedSensors] = useState(() => {
    const savedOrder = localStorage.getItem('sensorOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });

  const [showTooltipSensorId, setShowTooltipSensorId] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number, left: number }>({ top: 0, left: 0 });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const hideTooltipTimeout = useRef<NodeJS.Timeout | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);
  const [activeAlarmForTooltip, setActiveAlarmForTooltip] = useState<any>(null);

  const [acknowledgeAlarmLog] = useAcknowledgeAlarmLogMutation();

  const [ackState, setAckState] = useState<{[key: number]: {isLoading: boolean, isSuccess: boolean}}>({});

  // Pre-alarm state logic
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Helper to get pre-alarm log and time left
  function getPreAlarm(sensorId: number) {
    const preAlarmLog = alarmLog.find(log => log.sensor_id === sensorId && !log.active && !log.acknowledged && !log.alarm_reset);
    if (!preAlarmLog) return { preAlarm: null, preAlarmTimeLeft: null };
    const alarmSetting = alarmSettingsMap[sensorId];
    const delayMin = Number(alarmSetting?.delay) || 0;
    const activated = new Date(preAlarmLog.activated).getTime();
    const timeLeft = activated + delayMin * 60 * 1000 - now;
    return { preAlarm: preAlarmLog, preAlarmTimeLeft: timeLeft };
  }

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--dragging-bg',
      document.documentElement.classList.contains('dark') ? '#1f2937' : '#f9fafb'
    );
  }, []);

  const { data: liveData } = useGetLiveDataQuery(undefined, {
    pollingInterval: 30000,
  });

  useEffect(() => {
    setOrderedSensors(sensors.map((sensor: Sensor, index: number) => ({ ...sensor, order: index })));
  }, [sensors]);

  const navigate = useNavigate();

  const handleRowClick = (sensorId: number) => {
    if (enableDragAndDrop) {
      navigate(`/sensors/${sensorId}`);
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      setIsDragging(false);
      return;
    }

    const items = Array.from(orderedSensors);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedSensors(items);
    setIsDragging(false);

    // Save order to localStorage
    const orderMap = new Map((items as Sensor[]).map((item, index) => [item.id.toString(), index]));
    localStorage.setItem('sensorOrder', JSON.stringify([...orderMap]));
  };

  // Find the latest alarm log entry for a sensor in alarm state
  function getLatestAlarm(sensorId: number) {
    const logs = alarmLog.filter(log => log.sensor_id === sensorId && log.active && !log.acknowledged && !log.alarm_reset);
    if (logs.length === 0) return null;
    // Return the most recent
    return logs.reduce((a, b) => new Date(a.activated) > new Date(b.activated) ? a : b);
  }

  // Handler for acknowledging alarm
  const handleAcknowledge = async (sensorId: number) => {
    // Find one active, unacknowledged log for this sensor to get the event_id
    const logToAck = alarmLog.find(
      (log: any) => log.sensor_id === sensorId && log.active && !log.acknowledged && !log.alarm_reset
    );

    if (!logToAck || !logToAck.event_id) {
      console.error('No active, unacknowledged alarm with an event_id found for this sensor.');
      return;
    }

    const eventId = logToAck.event_id;
    setAckState(prev => ({ ...prev, [sensorId]: { isLoading: true, isSuccess: false } }));

    try {
      // Find all logs with the same event_id that need acknowledging
      const logsToAcknowledge = alarmLog.filter(
        (log: any) => log.event_id === eventId && !log.acknowledged
      );

      await Promise.all(
        logsToAcknowledge.map(l =>
          acknowledgeAlarmLog({
            logId: l.idAlarmLog,
            body: {
              acknowledged: true,
              acknowledged_by: user?.username || 'system',
              alarm_reset: new Date().toISOString()
            }
          })
        )
      );

      // After successful acknowledgment, update UI state
      setAckState(prev => ({ ...prev, [sensorId]: { isLoading: false, isSuccess: true } }));
      
      // Optionally, clear the success state after a delay and hide the tooltip
      setTimeout(() => {
        setAckState(prev => ({ ...prev, [sensorId]: { isLoading: false, isSuccess: false } }));
        setShowTooltipSensorId(null);
      }, 2000);
      
      // Invalidate alarm log data to refetch
      dispatch(alarmApi.util.invalidateTags(['Alarms']));

    } catch (error) {
      console.error('Failed to acknowledge alarm:', error);
      setAckState(prev => ({ ...prev, [sensorId]: { isLoading: false, isSuccess: false } }));
    }
  };

  const handleRowMouseEnter = (e: React.MouseEvent<HTMLTableRowElement>, sensorId: number) => {
    if (hideTooltipTimeout.current) clearTimeout(hideTooltipTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const latest = getLatestAlarm(sensorId);
    if (latest) {
      setShowTooltipSensorId(sensorId);
      setActiveAlarmForTooltip(latest);
      setTooltipPos({ top: rect.bottom + 8, left: rect.left });
    }
  };

  const handleRowMouseLeave = () => {
    // A small delay to allow moving mouse into tooltip
    hideTooltipTimeout.current = setTimeout(() => {
      if (!isTooltipHovered) setShowTooltipSensorId(null);
    }, 300);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTooltipTimeout.current) clearTimeout(hideTooltipTimeout.current);
    setIsTooltipHovered(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    hideTooltipTimeout.current = setTimeout(() => {
      setShowTooltipSensorId(null);
    }, 300);
  };

  // Add a no-op function for when drag-and-drop is disabled
  const noop = () => {};

  return (
    <DragDropContext onDragStart={enableDragAndDrop ? handleDragStart : noop} onDragEnd={enableDragAndDrop ? handleDragEnd : noop}>
      <div className="sm:overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed sm:table-auto">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th scope="col" className="w-[35%] sm:w-auto px-1 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Sensor Name
              </th>
              <th className="w-[30%] sm:w-auto px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Data
              </th>
              <th className="w-[20%] sm:w-auto px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="hidden md:table-cell px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last Seen
              </th>
              <th className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <Droppable droppableId="sensors" isDropDisabled={!enableDragAndDrop}>
            {(droppableProvided) => (
              <tbody
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}
                className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700"
              >
                {(orderedSensors as Sensor[])
                  .filter((sensor: Sensor) => sensor && sensor.id !== undefined && sensor.id !== null)
                  .map((sensor: Sensor, index: number) => {
                    const alarmState = getSensorAlarmState(sensor.id, alarmLog);
                    const latestAlarm = getLatestAlarm(sensor.id);
                    const { preAlarm, preAlarmTimeLeft } = getPreAlarm(sensor.id);
                    return (
                      <Draggable
                        key={sensor.id.toString()}
                        draggableId={sensor.id.toString()}
                        index={index}
                        isDragDisabled={!enableDragAndDrop}
                      >
                        {(draggableProvided, snapshot) => (
                          <SortableSensorRow
                            sensor={sensor}
                            liveData={liveData}
                            onRowClick={handleRowClick}
                            provided={draggableProvided}
                            isDragging={snapshot.isDragging}
                            latestAlarm={latestAlarm}
                            preAlarm={preAlarm}
                            preAlarmTimeLeft={preAlarmTimeLeft ?? 0}
                            onMouseEnter={(e) => {
                              handleRowMouseEnter(e, sensor.id);
                            }}
                            onMouseLeave={handleRowMouseLeave}
                            onUpdateFirmware={onUpdateFirmware}
                            showFirmwareUpdate={showFirmwareUpdate}
                            latestFirmwareVersion={latestFirmwareVersion}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            enableDragAndDrop={enableDragAndDrop}
                          />
                        )}
                      </Draggable>
                    );
                  })}
                {droppableProvided.placeholder}
              </tbody>
            )}
          </Droppable>
        </table>
      </div>
      {showTooltipSensorId !== null && (
        (() => {
          const { preAlarm, preAlarmTimeLeft } = getPreAlarm(showTooltipSensorId);
          if (preAlarm) {
            return ReactDOM.createPortal(
              <AlarmTooltip
                alarmDetails={preAlarm}
                position={tooltipPos}
                onAcknowledge={() => {}}
                isLoading={false}
                isSuccess={false}
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
                isPreAlarm={true}
                preAlarmTimeLeft={preAlarmTimeLeft ?? 0}
              />,
              document.body
            );
          } else if (activeAlarmForTooltip) {
            return ReactDOM.createPortal(
              <AlarmTooltip
                alarmDetails={activeAlarmForTooltip}
                position={tooltipPos}
                onAcknowledge={() => handleAcknowledge(showTooltipSensorId)}
                isLoading={!!ackState[showTooltipSensorId]?.isLoading}
                isSuccess={!!ackState[showTooltipSensorId]?.isSuccess}
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
              />,
              document.body
            );
          }
          return null;
        })()
      )}
    </DragDropContext>
  );
}